import { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useMediaQuery,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { descendingComparator } from '../document-index/DocumentIndexUtils';
import Button from '@material-ui/core/Button';
import ProjectProgressReportForm from './ProjectProgressReportForm';
import { createQRCode, parseDate } from '../../scripts/utils';
import { getNavigationState } from '../../features/navigation/selectors';
import Typography from '@material-ui/core/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-regular-svg-icons/faCopy';
import EmailUsersDialog from '../design/EmailUsersDialog';
import { getProjectPublicLink } from '../../models/api/project';
import {
  IProjectProgressReport,
  ProjectLifecycleFormType,
  ProjectUserPublicKeyAccessType,
} from '../../api-client/autogenerated';
import {
  deleteProjectProgressReportFormById,
  sendProjectLifecycleNotificationGroupEmails,
} from '../../models/api/project-lifecycle';
import { addSnackbar } from '../../features/snackbar/actions';
import { Add, Edit, MailOutline, Print } from '@material-ui/icons';
import EyeIcon from '@material-ui/icons/Visibility';
import DeleteIcon from '@material-ui/icons/Delete';
import { useReactToPrint } from 'react-to-print';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() =>
  createStyles({
    print: {
      '@media print': {
        margin: 16,
        paddingBottom: 64,
      },
    },
    printOnly: {
      display: 'none',
      '@media print': {
        display: 'block',
      },
    },
    printSpacer: {
      display: 'none',
      '@media print': {
        display: 'block',
        height: 32, // tweak this if needed
      },
    },
  }),
);

interface Props {
  canEdit: boolean;
  canDelete: boolean;
  publicData?: IProjectProgressReport | null;
  defaultReportId?: string | null;
  setIsLoading: (value: boolean) => void;
}

export default function ProjectProgressReportsTable(props: Props) {
  const { canEdit, canDelete, publicData, defaultReportId, setIsLoading } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const project = useSelector(getProjectState);
  const shouldBlockNavigation = useSelector(getNavigationState);

  const formRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery('(max-width: 800px)');

  const [isCreatingNewForm, setIsCreatingNewForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(defaultReportId || null);
  const [getALinkReportId, setGetALinkReportId] = useState<string | null>(null);
  const [publicLink, setPublicLink] = useState('');
  const [getALinkDialogOpen, setGetALinkDialogOpen] = useState(false);

  const print = useReactToPrint({
    content: () => formRef.current,
    bodyClass: 'print-mode',
  });

  useEffect(() => {
    if (selectedReportId === null) {
      setIsEditing(false);
    } else {
      getLink(selectedReportId).then(setPublicLink);
    }
  }, [selectedReportId]);

  const getLink = async (reportId: string) => {
    if (!project) return '';

    const { link } = await getProjectPublicLink(
      project.id,
      ProjectUserPublicKeyAccessType.ProgressReport,
      reportId,
    );

    return link;
  };

  const sendEmails = async (reportId: string) => {
    if (!project) return;

    const count = await sendProjectLifecycleNotificationGroupEmails(project.id, {
      formType: ProjectLifecycleFormType.Progress,
      progressReportId: reportId,
    });

    dispatch(
      addSnackbar({ id: Date.now(), message: `${count} emails sent!`, severity: 'success' }),
    );
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!project) return;

    const proceed = window.confirm(
      'Are you sure you want to delete this report? All of the data will be lost.',
    );
    if (!proceed) return;

    setIsLoading(true);

    try {
      await deleteProjectProgressReportFormById(project.id, reportId);

      window.location.reload();
    } finally {
      setIsLoading(false);
    }
  };

  const getActions = (report: IProjectProgressReport) => {
    const viewButton = (
      <Button
        variant="contained"
        color="primary"
        startIcon={<EyeIcon />}
        onClick={() => setSelectedReportId(report.id)}
      >
        View
      </Button>
    );

    const editButton = canEdit ? (
      <Button
        variant="contained"
        color="primary"
        startIcon={<Edit />}
        onClick={() => {
          setSelectedReportId(report.id);
          setIsEditing(true);
        }}
      >
        Edit
      </Button>
    ) : null;

    const deleteButton = canDelete ? (
      <Button
        variant="contained"
        color="primary"
        startIcon={<DeleteIcon />}
        onClick={() => handleDeleteReport(report.id)}
        style={{ color: 'white', backgroundColor: '#ED3F26' }}
      >
        Delete
      </Button>
    ) : null;

    const getALinkButton = (
      <Button
        variant="contained"
        color="primary"
        startIcon={
          <FontAwesomeIcon
            width={32}
            height={32}
            icon={faCopy}
            style={{ width: 20, height: 20, color: '#FFF' }}
          />
        }
        onClick={() => {
          setGetALinkReportId(report.id);
          setGetALinkDialogOpen(true);
        }}
        style={{ whiteSpace: 'nowrap' }}
      >
        Get A Link
      </Button>
    );

    const notifyButton = canEdit ? (
      <Button
        variant="contained"
        color="primary"
        startIcon={<MailOutline />}
        onClick={() => sendEmails(report.id)}
        style={{ whiteSpace: 'nowrap' }}
      >
        Notify Group
      </Button>
    ) : null;

    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'start',
          paddingBottom: 8,
          paddingTop: 8,
          gap: 8,
        }}
      >
        {viewButton}
        {editButton}
        {getALinkButton}
        {notifyButton}
        {deleteButton}
      </div>
    );
  };

  if (isCreatingNewForm || selectedReportId || publicData) {
    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 32,
            marginTop: 8,
          }}
        >
          {!publicData ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (shouldBlockNavigation) {
                  const proceed = window.confirm(
                    'You have unsaved changes. Are you sure you want to leave this page?',
                  );
                  if (!proceed) {
                    return;
                  }
                }
                setIsCreatingNewForm(false);
                setSelectedReportId(null);
                window.history.replaceState({}, '', window.location.pathname);
              }}
            >
              Return to Table
            </Button>
          ) : null}
          <div style={{ display: 'inline-flex', flexGrow: 1 }} />
          {selectedReportId && canEdit ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              disabled={isEditing}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          ) : null}
          <IconButton onClick={print} style={{ padding: 0, marginLeft: 8 }}>
            <Print />
          </IconButton>
        </div>
        <div ref={formRef} className={classes.print}>
          <Typography
            className={classes.printOnly}
            style={{
              fontSize: '34px',
              lineHeight: '40px',
              fontWeight: 600,
              letterSpacing: '-0.6px',
              color: '#0947B9',
              marginBottom: 36,
              maxWidth: 'calc(100% - 120px)',
            }}
          >
            {project?.name} Project Progress Report
          </Typography>
          <div
            ref={(ref) => {
              if (ref)
                createQRCode({
                  qrcodeRef: ref,
                  link: publicLink,
                  width: 112,
                  height: 112,
                  drawer: 'svg',
                });
            }}
            className={classes.printOnly}
            style={{ position: 'absolute', top: 8, right: 8 }}
          />
          <ProjectProgressReportForm
            reportId={selectedReportId}
            canEdit={(canEdit && isEditing) || isCreatingNewForm}
            isEditing={isEditing || isCreatingNewForm}
            publicData={publicData?.data}
          />
        </div>
      </>
    );
  }

  return (
    <div style={{ marginTop: 8, paddingBottom: 16 }}>
      {canEdit ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={!isMobile ? <Add /> : undefined}
          onClick={() => setIsCreatingNewForm(true)}
          style={{ marginBottom: 32, whiteSpace: 'nowrap' }}
        >
          Create New Progress Report
        </Button>
      ) : null}
      <TableContainer component={Paper} style={{ boxShadow: '0px 5px 15px rgb(0, 0, 0, .15)' }}>
        <Table>
          <TableBody>
            {project?.projectProgressReports
              ?.filter((x) => !!x.data)
              .sort((a, b) => {
                const byReportDate = descendingComparator(a.data!, b.data!, 'reportDate');
                if (byReportDate !== 0) return byReportDate;

                return descendingComparator(a, b, 'createdOn');
              })
              .map((report, index) => {
                const backgroundColor = index % 2 === 0 ? 'white' : 'rgba(182,206,252,0.3)';
                return (
                  <>
                    <TableRow style={{ backgroundColor }}>
                      <TableCell style={{ paddingTop: 16 }}>
                        <Typography style={{ fontWeight: 600 }}>
                          {report.data?.reportDate
                            ? parseDate(report.data?.reportDate).format('MM/DD/YYYY')
                            : ''}
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell style={{ paddingTop: 16 }}>
                        <div style={{ display: 'flex', whiteSpace: 'pre' }}>
                          <Typography style={{ fontWeight: 600 }}>Submitted By: </Typography>
                          <Typography>{report.data?.submittedByName}, </Typography>
                          <Typography style={{ fontWeight: 500 }}>
                            {report.data?.submittedByCompany}
                          </Typography>
                        </div>
                      </TableCell>
                    </TableRow>
                    <TableRow style={{ backgroundColor }}>
                      <TableCell colSpan={3}>{getActions(report)}</TableCell>
                    </TableRow>
                  </>
                );
              })}
            {project?.projectProgressReports?.length === 0 ? (
              <Typography style={{ paddingLeft: 16, paddingBottom: 16, paddingTop: 16 }}>
                No progress reports exist yet.
              </Typography>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {getALinkReportId !== null ? (
        <EmailUsersDialog
          dialogOpen={getALinkDialogOpen}
          closeDialog={() => setGetALinkDialogOpen(false)}
          getLink={() => getLink(getALinkReportId!)}
        />
      ) : null}
    </div>
  );
}
